import { useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

export const MALDIVES_EVENT_IMAGE_ID = 'uyuk30duoe0r2zhwdyzy'

const RSVP_URL = 'https://www.eventbrite.com/e/a-night-in-the-maldives-tickets-1985686473696'

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(20)};
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0) 55%,
    rgba(0, 0, 0, 0.5) 100%
  );
`

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
`

function AgentHubMaldivesEventModal() {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const maldivesEventImageTitle = intl.formatMessage({
    id: 'OwSbHHwVPAy90I8jgHBm',
    defaultMessage: 'A Night in the Maldives',
    description: 'Accessible alt text for the hero image in the Agent Hub Maldives event modal',
  })
  const maldivesEventImage: App.Image = {
    id: MALDIVES_EVENT_IMAGE_ID,
    title: maldivesEventImageTitle,
  }

  const handleClose = useCallback(() => {
    resolve()
  }, [resolve])

  return (
    <ModalBase onClose={handleClose}>
      <ModalHeader
        isOverlay
        title=""
        onCloseButtonClick={handleClose}
      />
      <ModalBody>
        <ImageContainer>
          <ModalImageThumbnail image={maldivesEventImage} />
          <ImageOverlay>
            <Heading variant="heading3" colour="neutral-eight">
              {intl.formatMessage({
                id: 'OMIprDSgvl17XLIcfG2x',
                defaultMessage: 'Join us for an exclusive industry event at Luxury Escapes Bondi Junction',
                description: 'Hero title overlaid on the Maldives event modal image',
              })}
            </Heading>
            <EventDetails>
              <BodyText variant="medium" colour="neutral-eight" weight="bold">
                {intl.formatMessage({
                  id: 'd8kid6unqVkMiuGiOqax',
                  defaultMessage: 'Bondi Junction',
                  description: 'Store location name for the Agent Hub Maldives event modal',
                })}
              </BodyText>
              <BodyText variant="medium" colour="neutral-eight">
                {intl.formatMessage({
                  id: 'qFd6xZXAELXPDhsdPSUr',
                  defaultMessage: 'Wednesday, 22 April 2026',
                  description: 'Scheduled event date for the Agent Hub Maldives event modal',
                })}
              </BodyText>
              <BodyText variant="medium" colour="neutral-eight">
                {intl.formatMessage({
                  id: 'v1Fw463IvHkbzQS_5_3z',
                  defaultMessage: '6:30 pm',
                  description: 'Scheduled event time for the Agent Hub Maldives event modal',
                })}
              </BodyText>
            </EventDetails>
          </ImageOverlay>
        </ImageContainer>
        <ModalContent>
          <VerticalSpacer gap={12}>
            <Heading variant="heading4" align="center">
              {intl.formatMessage({
                id: 'Z8ihddBuwtZzetjSb5JM',
                defaultMessage: 'Win a trip to the Maldives!',
                description: 'Main content heading in the Agent Hub Maldives event modal',
              })}
            </Heading>
            <BodyText variant="medium" align="center">
              {intl.formatMessage({
                id: '2J5ET1ZfYHygoyezlTNj',
                defaultMessage: 'Join industry experts at our Bondi Junction store for a Maldivian themed evening of drinks, canapés and luxury product updates.',
                description: 'Body description text for the Agent Hub Maldives event modal',
              })}
            </BodyText>
            <BodyText variant="medium" align="center">
              {intl.formatMessage({
                id: 'kXz12oWokcsU3moBNQdg',
                defaultMessage: 'Prizes to be won on the night include flights and accommodation.',
                description: 'Prizes information text for the Agent Hub Maldives event modal',
              })}
            </BodyText>
            <BodyText variant="medium" align="center">
              {intl.formatMessage(
                {
                  id: 'zvY0A3hVcNWklLD7EWbC',
                  defaultMessage: 'Limited spaces available – <rsvpLink>RSVP here</rsvpLink> to reserve your ticket',
                  description: 'RSVP call to action with link for the Maldives event modal',
                },
                {
                  rsvpLink: (chunks: React.ReactNode) => (
                    <TextLink key="rsvp" href={RSVP_URL} target="_blank">
                      {chunks}
                    </TextLink>
                  ),
                },
              )}
            </BodyText>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default AgentHubMaldivesEventModal
